export interface ScenarioItem {
  id: string;
  scenario: string;
  scenarioTag: string;
  icon: string;
  xmValue: string;
  xmDetail: string;
  icmarketsValue: string;
  icmarketsDetail: string;
  winner: "xm" | "icmarkets" | "tie";
  reason: string;
}

export const scenarioData: ScenarioItem[] = [
  {
    id: "scalping",
    scenario: "High-Frequency Scalping / Day Trading",
    scenarioTag: "Scalping",
    icon: "ri-flashlight-line",
    xmValue: "EUR/USD spread ~1.0 (Standard)",
    xmDetail: "Zero commission on Standard accounts. Reliable execution with 99.35% fill rate. Ultra Low account offers spreads from 0.6 pips with zero commission — the tightest pure-spread option. Execution around 1 second under normal conditions.",
    icmarketsValue: "EUR/USD spread avg 0.1 (Raw Spread, MT)",
    icmarketsDetail: "True ECN pricing with raw interbank spreads from 0.0 pips. Average execution under 40ms — among the fastest in the industry. $3.50/side commission on Raw Spread MT, $3.00/side on cTrader Raw.",
    winner: "icmarkets",
    reason: "IC Markets Wins: Raw spreads from 0.0 pips + sub-40ms execution + cTrader platform make IC Markets the definitive scalper's broker. At 10+ lots/day, the all-in cost difference vs XM Standard can exceed $80 daily."
  },
  {
    id: "beginner",
    scenario: "Small Capital / Getting Started",
    scenarioTag: "Beginners",
    icon: "ri-seedling-line",
    xmValue: "Welcome bonus + deposit match, $5 min",
    xmDetail: "$30 no-deposit bonus + 100% welcome bonus up to $500. Start trading with as little as $5. Massive educational library with live webinars. Zero-commission Micro and Standard accounts.",
    icmarketsValue: "No bonuses, $200 min deposit",
    icmarketsDetail: "Standard account with commission-free trading from 0.8 pips. No welcome bonuses or deposit promotions. Strong educational content and demo accounts, but no risk-free trial money.",
    winner: "xm",
    reason: "XM Wins: $5 minimum deposit + $30 no-deposit bonus makes XM the clear winner for beginners. IC Markets' $200 minimum and zero bonus culture create a significantly higher barrier for new traders testing the waters."
  },
  {
    id: "swing",
    scenario: "Swing Trading / Position Holding",
    scenarioTag: "Swing Trading",
    icon: "ri-moon-line",
    xmValue: "ASIC/CySEC regulated, standard swaps",
    xmDetail: "Multi-regulated with solid investor protection. Standard overnight swap fees at industry average. 1,000+ instruments including stocks, forex, and commodities for diversified swing portfolios.",
    icmarketsValue: "ASIC/CySEC/FSA, 2,250+ instruments",
    icmarketsDetail: "ASIC-regulated since 2007 with top-tier oversight. 2,250+ tradable instruments across forex, indices, commodities, stocks, crypto, and bonds — significantly more diversification for swing traders. Competitive swap rates on major pairs.",
    winner: "icmarkets",
    reason: "IC Markets Edges It: 2,250+ instruments vs XM's 1,000+ gives swing traders vastly more opportunities. ASIC since 2007 with sub-40ms execution and no inactivity fee — ideal for position traders who hold for weeks or months."
  },
  {
    id: "news",
    scenario: "News Trading / High-Impact Events",
    scenarioTag: "News Events",
    icon: "ri-newspaper-line",
    xmValue: "99.35% execution rate, near-zero requotes",
    xmDetail: "99.35% of orders executed in under 1 second. Explicit no-requote policy. Rock-solid execution during NFP, FOMC, and black-swan events with strict slippage control.",
    icmarketsValue: "Sub-40ms execution, ECN transparency",
    icmarketsDetail: "Industry-leading execution speed under 40ms. ECN model means real market fills with both positive and negative slippage possible during extreme events. No dealing desk interference — pure market pricing.",
    winner: "xm",
    reason: "XM Wins: Fear no NFP. XM's explicit no-requote guarantee and 99.35% execution rate during news events is top-tier. IC Markets' ECN fills are faster but pricing can gap during extreme volatility. XM's controlled approach is safer for news traders."
  }
];

export interface DownsideItem {
  text: string;
  icon: string;
}

export interface BrokerHonestTake {
  pros: DownsideItem[];
  cons: DownsideItem[];
}

export const xmHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "$30 no-deposit bonus + 100% welcome bonus up to $500. Trade with the broker's money before risking your own.",
      icon: "ri-check-line"
    },
    {
      text: "99.35% order execution rate in under 1 second. Virtually zero requotes even during major news events.",
      icon: "ri-check-line"
    },
    {
      text: "Ultra Low account with zero commissions and spreads from 0.6 pips. Pure spread model, no hidden fees.",
      icon: "ri-check-line"
    },
    {
      text: "ASIC, CySEC, FSC multi-regulated. Segregated client funds and negative balance protection for maximum security.",
      icon: "ri-check-line"
    },
    {
      text: "Free VPS for EA traders + XMP loyalty points on every trade. Trade more, earn back more.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "Standard account spreads are merely average at 1.0 pip. Without bonuses, XM isn't the cheapest on raw spread cost alone.",
      icon: "ri-emotion-sad-line"
    },
    {
      text: "No cTrader platform support. Scalpers and professional traders who rely on Depth of Market and advanced order types are limited to MT4/MT5.",
      icon: "ri-stock-line"
    },
    {
      text: "Bonus terms come with trading volume requirements. The free money isn't withdrawable until you trade enough lots.",
      icon: "ri-file-list-3-line"
    }
  ]
};

export const icmarketsHonestTake: BrokerHonestTake = {
  pros: [
    {
      text: "Raw spreads from 0.0 pips with ECN execution under 40ms. Among the absolute fastest execution speeds in the retail forex industry.",
      icon: "ri-check-line"
    },
    {
      text: "2,250+ tradable instruments across forex, indices, commodities, stocks, crypto, futures, and bonds. Industry-leading asset diversity.",
      icon: "ri-check-line"
    },
    {
      text: "ASIC-regulated since 2007 with segregated client funds in top-tier banks. Deep regulatory track record spanning nearly 20 years.",
      icon: "ri-check-line"
    },
    {
      text: "cTrader platform included at no extra cost with $3/side commission — the lowest commission rate of any IC Markets account type.",
      icon: "ri-check-line"
    },
    {
      text: "Zero inactivity fees on global accounts. Leave your account dormant and IC Markets won't nickel-and-dime you with monthly charges.",
      icon: "ri-check-line"
    }
  ],
  cons: [
    {
      text: "$200 minimum deposit across all account types. Significantly higher barrier to entry than XM's $5 — IC Markets is not built for micro-budget beginners.",
      icon: "ri-alert-line"
    },
    {
      text: "Absolutely zero bonuses or promotional offers. No welcome bonus, no deposit match, no loyalty points. Pure ECN — you get what you pay for.",
      icon: "ri-gift-line"
    },
    {
      text: "No stock exchange trading. IC Markets only offers CFDs — you cannot buy physical shares or receive dividend entitlements like XM's Shares account.",
      icon: "ri-stock-line"
    }
  ]
};

export interface AccountMatchup {
  id: string;
  title: string;
  subtitle: string;
  xmAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  icmarketsAccount: {
    name: string;
    spread: string;
    commission: string;
    minDeposit: string;
    leverage: string;
    highlight: string;
    pros: string[];
  };
  analysis: string;
}

export const accountMatchups: AccountMatchup[] = [
  {
    id: "entry",
    title: "The Entry-Level Face-Off: Where Beginners Land",
    subtitle: "XM Micro Account vs IC Markets Standard Account — the first account most traders sign up for",
    xmAccount: {
      name: "XM Micro",
      spread: "EUR/USD from 1.0 pip",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "1:1000",
      highlight: "1 micro lot = 1,000 units",
      pros: ["$5 minimum — lowest in the industry", "1 micro lot trading for risk management practice", "Zero commission — all cost is the spread", "Eligible for $30 no-deposit welcome bonus"]
    },
    icmarketsAccount: {
      name: "IC Markets Standard",
      spread: "EUR/USD from 0.8 pips",
      commission: "Zero commission",
      minDeposit: "$200",
      leverage: "1:500 (Global)",
      highlight: "Commission-free ECN spreads",
      pros: ["From 0.8 pips — tighter than XM Standard by nearly half", "Zero commission — simple cost structure", "ECN execution backbone under the hood", "Full MT4/MT5 access with 2,250+ instruments"]
    },
    analysis: "XM Micro crushes it on accessibility: $5 vs $200 minimum deposit is a 40x difference. But IC Markets Standard starts from 0.8 pips — tighter than XM Standard's 1.0 pip spread — so the per-trade cost is lower. If you can afford the $200 deposit, IC Markets Standard gives you tighter spreads and more instruments. If you're starting with pocket change, XM is the only option."
  },
  {
    id: "flagship",
    title: "The Flagship Showdown: Popular Accounts Head-to-Head",
    subtitle: "XM Standard vs IC Markets Raw Spread MT — the most-used accounts, stripped to the bone",
    xmAccount: {
      name: "XM Standard",
      spread: "EUR/USD from 1.0 pip",
      commission: "Zero commission",
      minDeposit: "$5",
      leverage: "1:1000",
      highlight: "Bonus-eligible flagship",
      pros: ["Zero commission — pay only the spread", "Eligible for $30 no-deposit + 100% welcome bonus", "99.35% execution rate under 1 second", "XMP loyalty points on every trade"]
    },
    icmarketsAccount: {
      name: "IC Markets Raw Spread (MT)",
      spread: "EUR/USD from 0.0 pips, avg 0.1",
      commission: "$3.50/side ($7/lot round-turn)",
      minDeposit: "$200",
      leverage: "1:500 (Global)",
      highlight: "True ECN — raw interbank pricing",
      pros: ["Raw spreads from 0.0 pips — true interbank pricing", "Average execution under 40ms — industry-leading speed", "Market depth visibility (Level 2 pricing)", "All strategies welcome — scalping, hedging, EAs, algos"]
    },
    analysis: "Here's the hard math: XM Standard at 1.0 pip = ~$10/lot all-in. IC Markets Raw Spread MT at avg 0.1 pips + $7/lot commission = ~$8/lot all-in. IC Markets is cheaper per trade at normal volumes. But XM's bonus program can tilt the equation — deposit $500 and get $500 bonus to trade with $1,000. For active traders doing 5+ lots/day, IC Markets' cost advantage compounds fast. For casual traders, XM's bonus + simpler pricing wins."
  },
  {
    id: "tightest",
    title: "The Tightest Spreads Battle: Who's Cheapest Per Pip?",
    subtitle: "XM Ultra Low vs IC Markets cTrader Raw — the lowest-cost accounts, fighting for every tenth of a pip",
    xmAccount: {
      name: "XM Ultra Low",
      spread: "EUR/USD from 0.6 pips",
      commission: "Zero commission",
      minDeposit: "$50",
      leverage: "1:1000",
      highlight: "XM's tightest — zero commission",
      pros: ["From 0.6 pips — XM's tightest pure-spread account", "Zero commission — no per-trade fees", "Available in Micro + Standard lot sizes", "$50 minimum — accessible for moderate traders"]
    },
    icmarketsAccount: {
      name: "IC Markets cTrader Raw",
      spread: "EUR/USD from 0.0 pips, avg 0.1",
      commission: "$3.00/side ($6/lot round-turn)",
      minDeposit: "$200",
      leverage: "1:500 (Global)",
      highlight: "Lowest IC commission rate",
      pros: ["Raw spreads from 0.0 pips — pure interbank pricing", "Cheapest commission: $3/side vs $3.50 on MT", "cTrader platform — built for precision trading", "Level 2 depth of market + advanced order types"]
    },
    analysis: "Final cost math: XM Ultra Low at 0.6 pips = ~$6/lot all-in (zero comm). IC Markets cTrader Raw at avg 0.1 pips + $6/lot commission = ~$7/lot all-in. At 1-5 lots/day, XM Ultra Low is marginally cheaper. But at 10+ lots/day, IC Markets' average 0.1 pip spread means your fills are consistently 0.5 pips tighter than XM — the compounding savings on spread alone can exceed $50/day for active scalpers. Plus you get cTrader's advanced order management."
  }
];

export interface TechComparison {
  category: string;
  icon: string;
  xm: { label: string; detail: string; score: number };
  icmarkets: { label: string; detail: string; score: number };
}

export const techData: TechComparison[] = [
  {
    category: "Trading Platform Options",
    icon: "ri-computer-line",
    xm: { label: "MT4/MT5 + TradingView", detail: "Full MT4 and MT5 support with MQL community signals. TradingView integration for chart-based analysis. No proprietary platform. Solid but conventional.", score: 7.0 },
    icmarkets: { label: "MT4/MT5 + cTrader + TradingView", detail: "All three major platforms: MT4/MT5, cTrader (both Raw and Standard), plus TradingView integration. cTrader is a significant advantage for precision traders with its advanced order types and Level 2 depth.", score: 9.5 }
  },
  {
    category: "Copy Trading Ecosystem",
    icon: "ri-group-line",
    xm: { label: "MQL5 Community Reliant", detail: "Copy trading primarily through MQL5 community signals. No dedicated proprietary copy trading platform. Functional but not specialized.", score: 5.5 },
    icmarkets: { label: "IC Social + cTrader Copy + ZuluTrade + Myfxbook", detail: "Five distinct copy trading channels: IC Social (mobile-first social trading), cTrader Copy (integrated strategy replication), ZuluTrade (third-party leaderboard), Myfxbook AutoTrade (verified performance tracking), and MT Signals. The most comprehensive copy ecosystem in the industry.", score: 9.0 }
  },
  {
    category: "Mobile App Experience",
    icon: "ri-smartphone-line",
    xm: { label: "Standard MT4/MT5 Mobile", detail: "Relies on standard MetaTrader mobile apps. Solid for order management and basic charting but no proprietary mobile experience.", score: 7.0 },
    icmarkets: { label: "cTrader Mobile + IC Social + MT Apps", detail: "cTrader mobile app with full charting and order management, IC Social for copy trading on-the-go, plus standard MT4/MT5 mobile. cTrader's mobile experience is widely considered superior to MT's mobile offering.", score: 8.5 }
  },
  {
    category: "API & Automation Support",
    icon: "ri-code-line",
    xm: { label: "MT4/MT5 Standard API", detail: "Full MQL4/MQL5 support with rich community resources. Free VPS for EA traders. No standalone REST API.", score: 7.5 },
    icmarkets: { label: "MQL + cTrader Automate + FIX API", detail: "MQL support for MT platforms, cTrader Automate (C# based — more powerful than MQL for complex algos), and FIX API access for institutional-grade low-latency trading. The most complete automation toolkit.", score: 9.0 }
  }
];

export interface PerkItem {
  icon: string;
  title: string;
  description: string;
}

export const xmPerks: PerkItem[] = [
  {
    icon: "ri-gift-2-line",
    title: "$30 No-Deposit Bonus",
    description: "Claim it right after verification. Trade real markets without depositing a cent first. The ultimate risk-free test drive."
  },
  {
    icon: "ri-vip-diamond-line",
    title: "Loyalty Points Program (XMP)",
    description: "Earn points on every trade. Redeem for cash or bonus credit. The more you trade, the more you earn back — effectively lowering your spread costs."
  },
  {
    icon: "ri-cloud-line",
    title: "Free VPS Service",
    description: "Qualify with minimum deposit and volume requirements. Perfect for traders running EAs 24/7 without keeping their PC on."
  },
  {
    icon: "ri-percent-line",
    title: "100% Welcome Deposit Bonus",
    description: "Double your first deposit up to $500. Deposit $500, trade with $1,000. The most aggressive bonus in the industry."
  }
];

export const icmarketsPerks: PerkItem[] = [
  {
    icon: "ri-speed-up-line",
    title: "Sub-40ms Execution Speed",
    description: "Average order execution under 40 milliseconds. Equinix NY4/LD5 data center proximity with fiber-optic cross-connects to liquidity providers. Among the fastest retail execution globally."
  },
  {
    icon: "ri-line-chart-line",
    title: "2,250+ Tradable Instruments",
    description: "Massive product range covering forex, indices, commodities, stocks, crypto, futures, and bonds. More than double XM's 1,000+ offering — unparalleled diversification for serious traders."
  },
  {
    icon: "ri-shield-star-line",
    title: "ASIC-Regulated Since 2007",
    description: "Nearly 20 years under Australia's top-tier regulator. Segregated client funds at AA-rated banks. Deep compliance track record that few brokers can match."
  },
  {
    icon: "ri-smartphone-line",
    title: "cTrader Platform Included Free",
    description: "Industry-leading cTrader platform with Level 2 pricing, advanced order types, and C#-based algorithmic trading. No extra cost — lower commission ($3/side) than the MT Raw account ($3.50/side)."
  }
];

export interface BlackSwanPoint {
  category: string;
  xm: string;
  icmarkets: string;
  xmAdvantage: boolean;
}

export const blackSwanData: BlackSwanPoint[] = [
  {
    category: "Order Execution (News Events)",
    xm: "99.35% of orders executed in under 1 second. Zero requotes policy. Rock-solid during NFP, FOMC, and black-swan market events.",
    icmarkets: "Sub-40ms average execution with true ECN fills. During extreme news events, spreads widen naturally as liquidity thins — real market behavior, not broker intervention. Both positive and negative slippage possible.",
    xmAdvantage: true
  },
  {
    category: "Slippage Management",
    xm: "Explicitly guarantees no requotes. Strict slippage control. Most orders filled at requested price or better during normal conditions.",
    icmarkets: "ECN model reflects genuine market depth. During fast markets, fills may slip but also offer positive slippage opportunities. Transparent but less controlled than XM's managed approach.",
    xmAdvantage: true
  },
  {
    category: "Fund Security & Compensation",
    xm: "Segregated client funds and negative balance protection. Multi-jurisdiction regulatory framework across ASIC, CySEC, and FSC.",
    icmarkets: "ASIC regulation since 2007 with segregated client money trust accounts at AA-rated banks. Professional indemnity insurance. Nearly 20-year operational track record with zero major regulatory incidents.",
    xmAdvantage: false
  },
  {
    category: "Platform Stability During Crises",
    xm: "MT4/MT5 infrastructure is battle-tested. During black-swan events, server stability is reliable but execution speed can slow under extreme load.",
    icmarkets: "Equinix NY4/LD5 hosting with dedicated fiber cross-connects. Multiple redundant servers across data centers. cTrader's infrastructure is independently maintained, providing platform diversity during outages — if MT goes down, cTrader is still live.",
    xmAdvantage: false
  }
];

export interface QuizQuestion {
  id: string;
  question: string;
  options: { label: string; value: string }[];
}

export const quizQuestions: QuizQuestion[] = [
  {
    id: "capital",
    question: "What's your starting capital?",
    options: [
      { label: "Under $200 — Just testing the waters", value: "small" },
      { label: "$200 - $5,000 — Got a decent budget", value: "medium" },
      { label: "$5,000+ — Professional level", value: "large" }
    ]
  },
  {
    id: "style",
    question: "What's your trading style?",
    options: [
      { label: "Scalping / High-frequency — Dozens to hundreds of trades daily", value: "scalping" },
      { label: "Day trading — A few trades per day", value: "daytrade" },
      { label: "Swing trading / Position holding — Days to weeks", value: "swing" }
    ]
  },
  {
    id: "priority",
    question: "What matters most to you?",
    options: [
      { label: "Lowest possible trading costs and fastest execution — every millisecond counts", value: "cost" },
      { label: "Bonuses, promotions, and free money to boost my account", value: "bonus" },
      { label: "Maximum platform choice and instrument diversity", value: "diversity" }
    ]
  }
];

export function computeQuizResult(answers: Record<string, string>): { broker: "xm" | "icmarkets"; percentage: number; reason: string } {
  const { capital, style, priority } = answers;
  let xmScore = 0;
  let icScore = 0;

  if (style === "scalping") {
    icScore += 35;
    xmScore += 10;
  } else if (style === "daytrade") {
    icScore += 20;
    xmScore += 20;
  } else {
    xmScore += 20;
    icScore += 10;
  }

  if (capital === "small") {
    xmScore += 35;
    icScore += 5;
  } else if (capital === "large") {
    icScore += 20;
    xmScore += 10;
  } else {
    icScore += 15;
    xmScore += 15;
  }

  if (priority === "cost") {
    icScore += 30;
    xmScore += 5;
  } else if (priority === "bonus") {
    xmScore += 30;
    icScore += 0;
  } else {
    icScore += 20;
    xmScore += 10;
  }

  const total = xmScore + icScore;
  if (xmScore >= icScore) {
    return {
      broker: "xm",
      percentage: Math.round((xmScore / total) * 100),
      reason: xmScore > icScore * 1.2
        ? "Your trading style is a strong match for XM: low entry barrier, generous bonuses, and rock-solid execution during news events. Perfect for beginners, bonus hunters, and news traders."
        : "You lean toward XM, but IC Markets is a solid alternative. XM's $5 minimum deposit and bonus program give you an edge — start with the $30 no-deposit bonus to test the waters risk-free."
    };
  }
  return {
    broker: "icmarkets",
    percentage: Math.round((icScore / total) * 100),
    reason: icScore > xmScore * 1.3
      ? "Your trading style is built for IC Markets: raw ECN spreads from 0.0 pips, sub-40ms execution, cTrader platform, and 2,250+ instruments. High-frequency and precision traders will find their optimal setup here."
      : "You lean toward IC Markets, but XM's bonuses and lower minimum deposit are worth considering. Try IC Markets' cTrader Raw account for the lowest commission rate ($3/side) in their lineup."
  };
}

export interface AccountTypeItem {
  name: string;
  highlight: string;
  spread: string;
  commission: string;
  minDeposit: string;
  leverage: string;
  note: string;
  features: string[];
  bestFor: string;
}

export const xmAccountTypes: AccountTypeItem[] = [
  {
    name: "Micro Account",
    highlight: "Ultra-low entry barrier",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "Contracts are 1,000 base currency units (1 micro lot). Perfect for absolute beginners who want to learn with minimal risk. Same execution quality as larger accounts — no discrimination based on account size.",
    features: [
      "Trade with as little as $5 — the industry's lowest barrier",
      "1 micro lot = 1,000 units, ideal for position sizing practice",
      "Full MT4/MT5 access with all 1,000+ instruments",
      "Negative balance protection included standard",
      "Same spreads and execution as Standard account"
    ],
    bestFor: "Complete beginners testing the waters with pocket change"
  },
  {
    name: "Standard Account",
    highlight: "Most popular — classic spread-only",
    spread: "From 1.0 pip (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$5",
    leverage: "Up to 1:1000",
    note: "XM's flagship account. 1 standard lot = 100,000 units. No commissions, just the raw spread. This is the account most XM traders use — reliable, simple, and backed by 99.35% execution rates.",
    features: [
      "Zero commission — pay only the spread, nothing more",
      "1 standard lot = 100,000 units, full-size trading",
      "Eligible for all XM bonuses including $30 no-deposit",
      "Free daily market analysis and webinars",
      "XMP loyalty points earned on every trade"
    ],
    bestFor: "The everyday trader who wants bonus perks + no commission complexity"
  },
  {
    name: "Ultra Low Account",
    highlight: "Tightest XM spreads — zero commission",
    spread: "From 0.6 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$50",
    leverage: "Up to 1:1000",
    note: "XM's tightest spread offering. Spreads start at 0.6 pips on EUR/USD — nearly half the Standard account — with zero commissions. Available in both Micro and Standard lot sizes. For moderate to active traders who want tight pricing without per-trade commission calculations.",
    features: [
      "Spreads from 0.6 pips — XM's tightest pure-spread offering",
      "Zero commission — all cost is in the spread",
      "Available in Micro + Standard lot sizes",
      "Same 1,000+ instruments and platform access",
      "Ideal sweet spot between Micro and high-volume accounts"
    ],
    bestFor: "Cost-aware traders wanting tight spreads without commission math"
  },
  {
    name: "Shares Account",
    highlight: "1,200+ global stocks — exchange pricing",
    spread: "Exchange-level real stock spreads",
    commission: "Per-share commission (varies by exchange)",
    minDeposit: "$10,000",
    leverage: "No leverage (physical shares)",
    note: "XM's dedicated stock trading account. Direct access to 1,200+ individual shares across US, UK, and EU exchanges — not CFDs, actual share trading with ownership rights. Leverage is not available. Designed for investors who want exchange-grade pricing and dividend entitlements.",
    features: [
      "Trade 1,200+ individual shares across global exchanges",
      "Exchange-grade pricing with transparent per-share commissions",
      "Dividend rights included on long positions",
      "Access to US, UK, EU stock exchanges",
      "Physical share ownership — different from CFDs"
    ],
    bestFor: "Stock investors wanting real exchange access, not CFD exposure"
  }
];

export const icmarketsAccountTypes: AccountTypeItem[] = [
  {
    name: "Standard Account",
    highlight: "Commission-free ECN spreads",
    spread: "From 0.8 pips (EUR/USD)",
    commission: "Zero commission",
    minDeposit: "$200",
    leverage: "Up to 1:500 (Global)",
    note: "IC Markets' entry-level account with commission-free trading. Spreads from 0.8 pips — significantly tighter than XM's Standard account at 1.0 pip. Backed by the same ECN execution infrastructure as the Raw accounts. Ideal for traders who want tight spreads without per-trade commission calculations.",
    features: [
      "From 0.8 pips — tighter than most brokers' standard accounts",
      "Zero commission — pay only the spread, nothing more",
      "ECN execution backbone with sub-40ms average fills",
      "Full MT4/MT5 access with 2,250+ instruments",
      "All strategies welcome — scalping, hedging, EAs"
    ],
    bestFor: "Traders wanting tight spreads without commission complexity"
  },
  {
    name: "Raw Spread Account (MT)",
    highlight: "True ECN — raw interbank pricing",
    spread: "From 0.0 pips (EUR/USD)",
    commission: "$3.50 per side ($7 per round turn)",
    minDeposit: "$200",
    leverage: "Up to 1:500 (Global)",
    note: "IC Markets' flagship MetaTrader account. True ECN with raw interbank spreads from 0.0 pips plus $3.50/side commission. Level 2 market depth visibility. This is the account that built IC Markets' reputation — consistently ranked among the top ECN brokers globally for tight pricing and fast execution.",
    features: [
      "Raw interbank spreads from 0.0 pips — true ECN pricing",
      "$3.50/side commission — competitive industry rate",
      "Market depth visibility (Level 2 pricing)",
      "Sub-40ms average execution speed",
      "MT4/MT5 with all EAs and strategies supported"
    ],
    bestFor: "Active traders and scalpers demanding raw interbank pricing on MT4/MT5"
  },
  {
    name: "cTrader Raw Account",
    highlight: "Lowest commission + cTrader platform",
    spread: "From 0.0 pips (EUR/USD)",
    commission: "$3.00 per side ($6 per round turn)",
    minDeposit: "$200",
    leverage: "Up to 1:500 (Global)",
    note: "Same raw ECN pricing as the MT Raw account, but on the cTrader platform with an even lower commission rate ($3.00/side vs $3.50/side). cTrader brings advanced order types, detachable charts, Level 2 depth, and C#-based algorithmic trading. The cheapest all-in trading cost of any IC Markets account — favored by precision scalpers and algo traders.",
    features: [
      "Lowest commission rate: $3.00/side ($6 round-turn)",
      "cTrader platform — advanced order types + detachable charts",
      "Level 2 market depth with full DOM visibility",
      "C#-based cTrader Automate for algorithmic trading",
      "Raw spreads from 0.0 pips — same interbank pricing"
    ],
    bestFor: "Precision traders and algo developers wanting the lowest costs + cTrader"
  }
];